import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { MainLayout } from '../components/layout/MainLayout';
import { CabecalhoPagina, FaixaFiltros } from '../components/layout/CabecalhoPagina';
import { EstadoVazio, EstadoCarregando } from '../components/common/Estados';
import { championshipApi } from '../lib/api';
import { statusCampeonato } from '../lib/statusCampeonato';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { 
  Trophy, 
  Plus, 
  Search, 
  Calendar,
  MapPin,
  MoreVertical,
  Edit,
  Trash2,
  Eye,
  LogIn
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { toast } from 'sonner';

export default function ChampionshipsPage() {
  const { user } = useAuth();
  const isSuperAdmin = user?.role === 'super_admin';
  const isFederation = user?.role === 'federation';
  const canManage = isSuperAdmin || isFederation;
  // Federation organizers can only edit/delete championships they themselves created
  const canManageThis = (champ) => isSuperAdmin || (isFederation && champ?.created_by === user?._id);
  const [championships, setChampionships] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const navigate = useNavigate();

  const fetchChampionships = async () => {
    try {
      const params = statusFilter !== 'all' ? { status: statusFilter } : {};
      const { data } = await championshipApi.list(params);
      setChampionships(data);
    } catch (error) {
      toast.error('Erro ao carregar campeonatos');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchChampionships();
  }, [statusFilter]);

  const handleDelete = async (id) => {
    if (!window.confirm('Tem certeza que deseja excluir este campeonato?')) return;
    
    try {
      await championshipApi.delete(id);
      toast.success('Campeonato excluido com sucesso');
      fetchChampionships();
    } catch (error) {
      toast.error('Erro ao excluir campeonato');
    }
  };

  const filteredChampionships = championships.filter(c =>
    c.name.toLowerCase().includes(search.toLowerCase())
  );

  const statusLabels = {
    open: 'Inscricoes Abertas',
    closed: 'Inscricoes Encerradas',
    in_progress: 'Em Andamento',
    finished: 'Finalizado'
  };

  return (
    <MainLayout>
      <div className="space-y-6" data-testid="championships-page">
        <CabecalhoPagina
          contexto="Principal"
          titulo="Campeonatos"
          descricao="Gerencie os campeonatos de Jiu-Jitsu"
          icone={Trophy}
          acoes={
            canManage && (
              <Button
                onClick={() => navigate('/championships/new')}
                data-testid="new-championship-btn"
                className="bg-[#D4AF37] text-black hover:bg-[#E5C158] font-bold"
              >
                <Plus size={20} className="mr-2" />
                Novo Campeonato
              </Button>
            )
          }
        />

        {/* Filtros em faixa própria (Etapa Visual 2) */}
        <FaixaFiltros rotulo="Buscar e filtrar">
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-[#71717A]" size={20} />
            <Input
              placeholder="Buscar campeonatos..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              data-testid="search-championships"
              className="pl-10 bg-[#141414] border-[#27272A] text-white placeholder:text-[#71717A]"
            />
          </div>
          <div className="flex gap-2">
            {['all', 'open', 'in_progress', 'finished'].map((status) => (
              <Button
                key={status}
                variant={statusFilter === status ? 'default' : 'outline'}
                onClick={() => setStatusFilter(status)}
                data-testid={`filter-${status}`}
                className={statusFilter === status 
                  ? 'bg-[#D4AF37] text-black hover:bg-[#E5C158]' 
                  : 'border-[#27272A] text-[#A1A1AA] hover:text-white hover:border-[#D4AF37]'
                }
              >
                {status === 'all' ? 'Todos' : statusLabels[status]}
              </Button>
            ))}
          </div>
        </div>
        </FaixaFiltros>

        {/* Championships Grid */}
        {loading ? (
          <EstadoCarregando />
        ) : filteredChampionships.length === 0 ? (
          <Card className="bg-[#141414] border-[#27272A]">
            <CardContent>
              <EstadoVazio
                icone={Trophy}
                titulo="Nenhum campeonato encontrado"
                descricao="Crie seu primeiro campeonato para comecar"
                acao={
                  <Button
                    onClick={() => navigate('/championships/new')}
                    className="bg-[#D4AF37] text-black hover:bg-[#E5C158]"
                  >
                    <Plus size={16} className="mr-2" />
                    Criar Campeonato
                  </Button>
                }
              />
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {filteredChampionships.map((champ) => (
              <Card 
                key={champ._id}
                className="bg-[#141414] border-[#27272A] card-hover overflow-hidden"
                data-testid={`championship-card-${champ._id}`}
              >
                {/* A faixa do topo deixou de ser decoração: agora ela DIZ o
                    estado do campeonato (verde=aberto, azul=andamento,
                    cinza=finalizado) — a mesma cor do selo ao lado. */}
                <div className={statusCampeonato(champ.status).barra} aria-hidden="true" />
                <CardContent className="p-5">
                  <div className="mb-4 flex items-start justify-between gap-3">
                    <div className="flex min-w-0 items-center gap-3">
                      <span className="medalhao medalhao--suave" aria-hidden="true">
                        <Trophy size={20} />
                      </span>
                      <div className="min-w-0">
                        <h3 className="truncate font-bold text-white">{champ.name}</h3>
                        <span className={`${statusCampeonato(champ.status).selo} mt-1.5`}>
                          {statusCampeonato(champ.status).rotulo}
                        </span>
                      </div>
                    </div>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="icon" aria-label="Mais opções" className="text-[#71717A] hover:text-white">
                          <MoreVertical size={20} />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent className="bg-[#1A1A1A] border-[#27272A]">
                        <DropdownMenuItem 
                          onClick={() => navigate(`/championships/${champ._id}`)}
                          className="text-white hover:bg-[#27272A] cursor-pointer"
                        >
                          <Eye size={16} className="mr-2" /> Ver detalhes
                        </DropdownMenuItem>
                        {canManageThis(champ) && (
                          <DropdownMenuItem 
                            onClick={() => navigate(`/championships/${champ._id}/edit`)}
                            className="text-white hover:bg-[#27272A] cursor-pointer"
                          >
                            <Edit size={16} className="mr-2" /> Editar
                          </DropdownMenuItem>
                        )}
                        {canManageThis(champ) && (
                          <DropdownMenuItem 
                            onClick={() => handleDelete(champ._id)}
                            className="text-red-400 hover:bg-red-400/10 cursor-pointer"
                          >
                            <Trash2 size={16} className="mr-2" /> Excluir
                          </DropdownMenuItem>
                        )}
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>

                  {/* 2 chips de metadado — mesma informação que antes, agora
                      numa linha só em vez de duas linhas de altura total. */}
                  <div className="flex flex-wrap gap-2">
                    {champ.date && (
                      <span className="inline-flex items-center gap-1.5 rounded-lg border border-[#27272A] bg-[#1A1A1A] px-2.5 py-1 text-xs text-[#A1A1AA]">
                        <Calendar size={12} className="text-[#D4AF37]" aria-hidden="true" />
                        {champ.date}
                      </span>
                    )}
                    {champ.address && (
                      <span className="inline-flex min-w-0 items-center gap-1.5 rounded-lg border border-[#27272A] bg-[#1A1A1A] px-2.5 py-1 text-xs text-[#A1A1AA]">
                        <MapPin size={12} className="text-[#D4AF37]" aria-hidden="true" />
                        <span className="truncate">{champ.address}</span>
                      </span>
                    )}
                  </div>

                  {/* 1 ação visível: o olho duplicava exatamente este "Entrar"
                      (mesma rota); "Ver detalhes" continua no menu de opções. */}
                  <div className="mt-4">
                    <Button
                      onClick={() => navigate(`/championships/${champ._id}`)}
                      className="w-full bg-[#D4AF37] text-black hover:bg-[#E5C158] font-bold"
                    >
                      <LogIn size={16} className="mr-2" />
                      Entrar
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </MainLayout>
  );
}
